import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { LockKeyIcon, CreditCardIcon, XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

/**
 * AddCardDialog — Stripe Elements modal that captures a card via a
 * SetupIntent so we can charge it later.  Card data is collected client-
 * side by Stripe.js, never hits our backend.
 *
 * Flow:
 *   1. POST /api/me/wallet/setup-intent → { client_secret, publishable_key }
 *   2. loadStripe(publishable_key) → Elements provider
 *   3. stripe.confirmCardSetup(client_secret, { payment_method: { card } })
 *   4. onSaved() — parent re-fetches the wallet list and closes the modal.
 */

function CardForm({ onCancel, onSaved, cardholderDefault, clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState(cardholderDefault || "");
  const [busy, setBusy] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy || !stripe || !elements) return;
    const card = elements.getElement(CardElement);
    if (!card) return;
    if (!name.trim()) {
      setError("Cardholder name is required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { error: stripeErr, setupIntent } =
        await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card,
            billing_details: { name: name.trim() },
          },
        });
      if (stripeErr) {
        setError(stripeErr.message || "Could not save card. Try again.");
        return;
      }
      if (setupIntent && setupIntent.status === "succeeded") {
        toast.success("Card saved");
        onSaved?.(setupIntent.payment_method);
      } else {
        setError("Card setup did not finish. Try again.");
      }
    } catch (err) {
      setError(err?.message || "Card setup failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="add-card-form">
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-white/55">
          Name on card
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe"
          autoComplete="cc-name"
          maxLength={80}
          className="mt-1 w-full h-11 px-3 rounded-lg bg-white/5 border border-white/15 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#1D78AB]"
          data-testid="add-card-name"
        />
      </div>

      <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-white/55">
          Card details
        </label>
        <div
          className="mt-1 rounded-lg bg-white/5 border border-white/15 px-3 py-3 focus-within:border-[#1D78AB]"
          data-testid="add-card-element-wrapper"
        >
          <CardElement
            options={{
              hidePostalCode: false,
              style: {
                base: {
                  fontSize: "15px",
                  color: "#ffffff",
                  fontFamily:
                    "'Cabinet Grotesk', 'General Sans', system-ui, sans-serif",
                  iconColor: "#9CA3AF",
                  "::placeholder": { color: "rgba(255,255,255,0.35)" },
                },
                invalid: { color: "#fda4af", iconColor: "#fda4af" },
              },
            }}
            onChange={(ev) => {
              setCardReady(ev.complete);
              if (ev.error) setError(ev.error.message || "");
              else setError("");
            }}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 text-[11px] text-white/55">
        <LockKeyIcon size={12} weight="fill" />
        Stripe-encrypted. Bidorah never sees your card number.
      </div>

      {error && (
        <div
          className="text-[12px] text-rose-300 font-semibold"
          data-testid="add-card-error"
        >
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex-1 h-11 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-bold disabled:opacity-50"
          data-testid="add-card-cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !stripe || !cardReady || !name.trim()}
          className="flex-1 h-11 rounded-full bg-amber-400 hover:bg-amber-300 text-zinc-900 text-sm font-black disabled:opacity-50"
          data-testid="add-card-save"
        >
          {busy ? "Saving…" : "Save card"}
        </button>
      </div>
    </form>
  );
}

export default function AddCardDialog({ open, onClose, onSaved, currentUser }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const startedRef = useRef(false);

  // On open, fetch a SetupIntent and lazily-load Stripe.js.
  useEffect(() => {
    if (!open || startedRef.current) return;
    startedRef.current = true;
    setLoadErr("");
    setClientSecret("");
    setStripePromise(null);
    (async () => {
      try {
        const { data } = await api.post("/me/wallet/setup-intent");
        if (!data.publishable_key) {
          setLoadErr("Card setup is not configured.");
          return;
        }
        setClientSecret(data.client_secret);
        setStripePromise(loadStripe(data.publishable_key));
      } catch (e) {
        setLoadErr(apiError(e));
      }
    })();
    return () => {
      startedRef.current = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setClientSecret("");
      setStripePromise(null);
    }
  }, [open]);

  const elementsOptions = useMemo(
    () => ({
      // Match the dark wallet sheet.
      appearance: { theme: "night", variables: { colorPrimary: "#1D78AB" } },
    }),
    [],
  );

  if (!open) return null;

  return createPortal(
    <div
      // pointerEvents:auto explicitly opts out of the body-level
      // pointer-events:none lock that Radix Dialog (the parent Sheet)
      // applies while it's open — without this, none of the dialog's
      // buttons, inputs, or the Stripe CardElement iframe can receive
      // clicks/taps.  See test_reports/iteration_32 for the RCA.
      style={{ pointerEvents: "auto" }}
      className="fixed inset-0 z-[2100] grid place-items-end md:place-items-center bg-black/65"
      onClick={onClose}
      data-testid="add-card-dialog"
    >
      <div
        className="w-full md:max-w-md bg-zinc-950 text-white rounded-t-3xl md:rounded-2xl p-5 space-y-4 border-t border-white/10 md:border md:border-white/10 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 grid place-items-center rounded-full bg-amber-400 text-zinc-900">
              <CreditCardIcon size={20} weight="fill" />
            </span>
            <div>
              <div className="text-base font-black leading-tight">Add card</div>
              <div className="text-[11px] text-white/55 leading-tight">
                Saved securely with Stripe for one-tap bidding.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/15"
            aria-label="Close"
            data-testid="add-card-close"
          >
            <XIcon size={16} weight="bold" />
          </button>
        </div>

        {loadErr ? (
          <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-3 text-sm text-rose-200">
            {loadErr}
          </div>
        ) : !stripePromise || !clientSecret ? (
          <div className="py-10 text-center text-sm text-white/55">
            Preparing secure card form…
          </div>
        ) : (
          <Elements stripe={stripePromise} options={elementsOptions} key={clientSecret}>
            <CardForm
              onCancel={onClose}
              onSaved={(pmId) => onSaved?.(pmId)}
              cardholderDefault={currentUser?.name || ""}
              clientSecret={clientSecret}
            />
          </Elements>
        )}
      </div>
    </div>,
    document.body,
  );
}
